import { faCartPlus, faPlus, faSave, faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import {
	Button,
	Checkbox,
	DropDownButton,
	DropDownOption,
	DropDownSub,
	FormErrorMessage,
	NativeInput,
	ProductionUnit,
	Tooltip,
	Typography,
	useMediaQuery,
} from "@tosize/ui";
import { Fragment, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAddToCartContext } from "../../../context/AddToCartContext";
import { useConfig } from "../../../context/ConfigContext";
import { useErrorContext } from "../../../context/ErrorContext";
import { usePostDataContext } from "../../../context/PostDataContext";
import useAgreement from "../../../hooks/useAgreement";
import useDimensionsForm from "../../../hooks/useDimensionsForm";
import useProducts from "../../../hooks/useProducts";
import useShapes from "../../../hooks/useShapes";

export function Quantity() {
	const intl = useIntl();
	const { mode, projects, customer } = useConfig();
	const { productData } = useProducts();
	const { workpiece, setWorkpiece, data, setData } = usePostDataContext();
	const { onAddToProjectClick, onAddToCartClick, isLoading } = useAddToCartContext();
	const { errors } = useErrorContext();
	const { validate } = useAgreement();
	const { shape } = useShapes();
	const { shapeModel } = useDimensionsForm(shape);

	const isSmallDevice = useMediaQuery({ max: "sm" });
	const isMediumDevice = useMediaQuery({ min: "sm", max: "lg" });
	const isLargeDevice = useMediaQuery({ min: "xl" });

	const selectedValue = useMemo(() => {
		const dimensionsString = shapeModel?.getDimensionsString(workpiece.shape_dimensions, intl);

		if (shape?.name && dimensionsString) {
			return `${shape?.name} | ${dimensionsString}`;
		}

		return shape?.name;
	}, [workpiece.shape_dimensions, intl, shape?.name, shapeModel]);

	const time = new Date().toLocaleTimeString(intl.locale, {
		hour: "2-digit",
		minute: "2-digit",
	});

	return (
		<>
			{productData?.agreement?.mandatory && (
				<Fragment>
					<Checkbox
						className="text-shades-dark"
						name="agreement"
						checked={workpiece.accepted_condition}
						label={productData?.agreement.text}
						onChange={async (e) => {
							setWorkpiece((prev) => ({
								...prev,
								accepted_condition: e.target.checked,
							}));

							await validate(e.target.checked);
						}}
					/>

					{errors.accepted_condition?.map((issue) => (
						<FormErrorMessage key={issue.code} variant="danger">
							{issue.message}
						</FormErrorMessage>
					))}
				</Fragment>
			)}

			<div className="flex flex-col items-center justify-center gap-2">
				<div className="flex w-full space-x-2">
					<NativeInput
						className="max-w-[4rem] py-1 sm:max-w-[6rem] sm:p-2"
						type="number"
						inputMode="numeric"
						step="1"
						min="1"
						value={data.quantity}
						onChange={(e) => setData((prev) => ({ ...prev, quantity: e.target.valueAsNumber }))}
					/>

					<DropDownButton
						className="w-full"
						color="success"
						size={isSmallDevice ? "medium" : "large"}
						disabled={isLoading}
						dropdown={
							<>
								<DropDownOption
									title={intl.formatMessage({
										defaultMessage: "Toevoegen aan winkelwagen",
										id: "uCt4yW",
									})}
									description={intl.formatMessage({
										defaultMessage: "Voeg dit item toe aan je winkelwagen en bestel direct",
										id: "lwdpe5",
									})}
									onClick={onAddToCartClick}
									disabled={isLoading}
									icon={mode === "editCartItem" || mode === "editProjectItem" ? faSave : faCartPlus}
								/>

								{customer ? (
									<DropDownSub
										icon={faPlus}
										title={intl.formatMessage({
											defaultMessage: "Toevoegen aan project",
											id: "VXb2uL",
										})}
										description={intl.formatMessage({
											defaultMessage:
												"Voeg dit item aan een (bestaand) project toe en bereid meerdere bestellingen in 1x voor",
											id: "u62Hri",
										})}
									>
										<>
											<DropDownOption
												icon={faPlus}
												title={intl.formatMessage({
													defaultMessage: "Toevoegen aan nieuw project",
													id: "+ekKk+",
												})}
												description={`${intl.formatMessage({
													defaultMessage: "Voeg dit item toe aan nieuw project",
													id: "aNU/Zq",
												})}: ${intl.formatMessage({ defaultMessage: "Project", id: "k36uSw" })} #${projects.length + 1}`}
												onClick={() =>
													onAddToProjectClick(
														`${intl.formatMessage({ defaultMessage: "Project", id: "k36uSw" })} #${projects.length + 1}`,
														"newProject",
													)
												}
											/>

											{projects.map((project, index) => (
												<DropDownOption
													key={index}
													title={project.name}
													description={intl.formatMessage(
														{
															defaultMessage: "Voeg dit item toe aan project: {value}",
															id: "+3zyG9",
														},
														{ value: project.name },
													)}
													onClick={() => onAddToProjectClick(project.name, project.id)}
												/>
											))}
										</>
									</DropDownSub>
								) : (
									<DropDownOption
										icon={faUserPlus}
										title={intl.formatMessage({
											defaultMessage: "Inloggen en toevoegen aan project",
											id: "wDbfld",
										})}
										description={intl.formatMessage({
											defaultMessage:
												"Log in en voeg daarna dit item aan een (bestaand) project toe en bereid meerdere bestellingen in 1x voor",
											id: "t1atEF",
										})}
										onClick={() =>
											onAddToProjectClick(
												`${intl.formatMessage({ defaultMessage: "Project", id: "k36uSw" })} #${time}`,
												"newProject",
											)
										}
									/>
								)}
							</>
						}
					>
						<Tooltip content={selectedValue} side="top" hideOnTouchDevice>
							<Button
								size={isSmallDevice ? "medium" : "large"}
								color="success"
								className="w-full"
								starticon={
									mode === "editCartItem" || mode === "editProjectItem" ? faSave : faCartPlus
								}
								isLoading={isLoading}
								onClick={onAddToCartClick}
							>
								{isMediumDevice || isLargeDevice ? (
									mode === "editCartItem" || mode === "editProjectItem" ? (
										<FormattedMessage defaultMessage="Aanpassingen opslaan" id="BS8esg" />
									) : (
										<FormattedMessage defaultMessage="Toevoegen aan winkelwagen" id="uCt4yW" />
									)
								) : mode === "editCartItem" || mode === "editProjectItem" ? (
									<FormattedMessage defaultMessage="Opslaan" id="Sxhdhg" />
								) : (
									<FormattedMessage defaultMessage="Toevoegen" id="O0INX/" />
								)}
							</Button>
						</Tooltip>
					</DropDownButton>
				</div>

				{data.unit === ProductionUnit.CompletePanel && mode === "add" && (
					<Typography color="text-shades-dark" size="text-sm">
						{intl.formatMessage({
							defaultMessage: "Bekijk zaagschema en plaatprijs in winkelwagen",
							id: "/mhi9e",
						})}
					</Typography>
				)}
			</div>
		</>
	);
}
